import type {
  OrderActivityComment,
  OrderActivityDeliveryBox,
  OrderActivityDetailTable,
  OrderActivityFactoryChange,
  OrderActivityFactoryTechpackPdfRow,
  OrderActivityFieldChange,
  OrderActivityImageRef,
  OrderActivityKind,
  OrderActivityLogDetail,
  OrderActivityLogListItem,
  OrderActivityMetadataLine,
  OrderActivityPriceBreakdownSnapshot,
  OrderActivityPriceUpdateDetail,
  OrderActivityPdfChange,
  OrderActivityQcPdfReport,
  OrderActivityReorderInfo,
  OrderActivitySamplePreviewCard,
  OrderActivityShippingSnapshot,
  OrderActivityStateEntry,
  OrderActivityTechpackPdfUrlsUpdate,
  OrderActivityTrackingLink,
} from '@/models/OrderActivityLog';

/**
 * Raw row returned by the admin endpoints:
 *  - GET /order-items/{order_item_id}/order-activity-logs
 *  - GET /order-activity-logs/{id}
 *
 * Mirrors `app/Models/OrderActivityLog.php` plus the eager-loaded relations from
 * `app/Services/OrderActivityLogAdminService::defaultWith()`.
 */
export interface BackendOrderActivityLog {
  id: number;
  user_id: number | null;
  related_type: string;
  related_id: number;
  order_item_activity_id: number | null;
  /** Either a value of `OrderActivityLogActionType` enum OR an order item status string (saveActivity flow). */
  action_type: string;
  activity_meta_data: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;

  user?: { id: number; name: string; email: string } | null;
  /**
   * The admin endpoints return Spatie role rows (array of objects) here.
   * Older / list-style payloads may surface a string accessor or null.
   */
  user_role?:
    | string
    | null
    | Array<{ id?: number; name?: string; label?: string } & Record<string, unknown>>;
  order_item_activity?: {
    id: number;
    order_item_id?: number;
    status: string;
    message?: string | null;
    created_at?: string;
    updated_at?: string;
  } | null;
}

type Meta = Record<string, any>;

const STORAGE_BASE = (import.meta.env.VITE_APP_FILE_URL ?? '').toString().replace(/\/+$/, '');

function s3Url(pathOrUrl: string | null | undefined): string | undefined {
  if (!pathOrUrl) return undefined;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return STORAGE_BASE ? `${STORAGE_BASE}/${pathOrUrl.replace(/^\/+/, '')}` : pathOrUrl;
}

function asMeta(row: BackendOrderActivityLog): Meta {
  const raw = (row.activity_meta_data ?? {}) as Meta;
  const merged = raw.order_activity_log_merged_fields;
  if (merged && typeof merged === 'object' && !Array.isArray(merged)) {
    const { order_activity_log_merged_fields: _drop, ...rest } = raw;
    return { ...(merged as Meta), ...rest };
  }
  return raw;
}

/** Non-empty trimmed string, or undefined (null / "" treated as absent). */
function optionalNonEmptyString(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value !== 'string') return undefined;
  const t = value.trim();
  return t.length ? t : undefined;
}

/** Non-empty arrays only (missing / [] → undefined). */
function safeNonEmptyArray<T>(value: unknown): T[] | undefined {
  if (!Array.isArray(value) || value.length === 0) return undefined;
  return value as T[];
}

/** Count object keys or array length (backend may send either for id maps). */
function collectionCount(value: unknown): number {
  if (Array.isArray(value)) return value.length;
  if (value && typeof value === 'object') return Object.keys(value as object).length;
  return 0;
}

/** Format id arrays for metadata (coerce elements to string). */
function formatIdList(ids: unknown): string | undefined {
  if (!Array.isArray(ids) || ids.length === 0) return undefined;
  const parts = ids
    .filter((x) => x !== null && x !== undefined && x !== '')
    .map((x) => String(x));
  return parts.length ? parts.join(', ') : undefined;
}

function joinNonEmpty(parts: Array<string | null | undefined>, sep = ' '): string {
  return parts.filter((p) => typeof p === 'string' && p.trim().length > 0).join(sep);
}

function formatAddress(addr: any): string {
  if (!addr) return '—';
  if (typeof addr === 'string') return addr;
  const line1 = addr.address ?? addr.address1 ?? addr.street;
  const line2 = addr.address_2 ?? addr.address2;
  const zip = addr.zip ?? addr.postalCode ?? addr.postal_code;
  const lines = [
    joinNonEmpty([addr.first_name, addr.last_name]),
    addr.name,
    addr.company,
    line1,
    line2,
    joinNonEmpty([addr.city, addr.state, zip], ', '),
    addr.country,
    addr.phone ? `Phone: ${addr.phone}` : null,
  ].filter((l) => l && String(l).trim().length > 0);
  return lines.join('\n') || '—';
}

function humanizeStatus(value: string | null | undefined): string {
  if (!value) return '—';
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function safeBool(value: any): boolean | undefined {
  if (value === true || value === false) return value;
  if (value === 1 || value === '1' || value === 'true') return true;
  if (value === 0 || value === '0' || value === 'false') return false;
  return undefined;
}

function toDisplayString(value: any): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return '—';
  }
}

/** True when an id/scalar is present (non-null, non-empty string after trim). */
function hasScalarValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'number') return Number.isFinite(value);
  return true;
}

/** activity_source values like assign_orders → “Assign orders” */
function humanizeActivitySource(source: string): string {
  return humanizeStatus(source.replace(/-/g, '_'));
}

/** Customer design-sample flows from portal (legacy + current API names). */
function isCustomerPortalSamples(meta: Meta): boolean {
  const s = meta.activity_source;
  return s === 'customer_portal_design_samples' || s === 'customer_portal';
}

/**
 * Order admin column: prefer name + optional ID; tolerate null/empty.
 * Supports optional previous_order_admin_name when backend sends it.
 */
function formatOrderAdminCell(meta: Meta, which: 'previous' | 'new'): string {
  const nameKey =
    which === 'previous' ? 'previous_order_admin_name' : 'new_order_admin_name';
  const idKey = which === 'previous' ? 'previous_order_admin_id' : 'new_order_admin_id';
  const rawName = meta[nameKey];
  const rawId = meta[idKey];
  const name = typeof rawName === 'string' ? rawName.trim() : '';
  const hasId = hasScalarValue(rawId) && String(rawId).trim() !== '';
  if (name && hasId) return `${name} (ID ${rawId})`;
  if (name) return name;
  if (hasId) return `ID ${rawId}`;
  return '—';
}

/**
 * Map (action_type, activity_source) -> frontend OrderActivityKind.
 * The action_type is either a value from `app/Enums/OrderActivityLogActionType.php`
 * or an order item status string (when emitted from the saveActivity() flow).
 *
 * Source-of-truth: every callsite of `OrderActivityLogService::log()` in the backend.
 */
function pickKind(actionType: string, meta: Meta): OrderActivityKind {
  // Source-aware overrides first (same action_type, different intent)
  if (actionType === 'address_updated' && meta.activity_source === 'my_task') {
    return 'shipping_address_my_task';
  }
  if (
    actionType === 'submitted_for_customer_review' &&
    meta.activity_source === 'third_party_email'
  ) {
    return 'third_party_approval';
  }
  if (
    (actionType === 'customer_approved' || actionType === 'customer_rejected') &&
    isCustomerPortalSamples(meta)
  ) {
    return 'customer_sample_action';
  }

  const direct: Record<string, OrderActivityKind> = {
    factory_assigned: 'factory_assigned',
    factory_changed: 'factory_edited',

    production_date_changed: 'production_date_change',
    shipping_details_updated: 'shipping_details',
    tracking_link_updated: 'qc_tracking_link',

    address_updated: 'details_tab_update',
    po_number_updated: 'details_tab_update',
    reference_number_updated: 'details_tab_update',
    roster_updated: 'details_tab_update',

    order_admin_assigned: 'order_admin_assigned',
    copied_order_without_admin: 'order_copied_without_admin',
    urgency_updated: 'urgency_updated',
    dqc_status_updated: 'dqc_status',

    techpack_edited: 'tech_pack',
    techpack_pdf_urls_updated: 'tech_pack',
    custom_pdf_uploaded: 'custom_pdf',
    custom_pdf_removed: 'custom_pdf',
    price_updated: 'price_breakdown',
    stripe_invoice_created: 'activity_flow',
    stripe_invoice_number_updated: 'activity_flow',

    internal_comment_added: 'internal_comment',
    internal_comment_updated: 'internal_comment',
    internal_comment_deleted: 'internal_comment',

    delivery_note_created: 'delivery_box',
    delivery_note_updated: 'delivery_box',
    delivery_note_deleted: 'delivery_box',
    delivery_box_updated: 'delivery_box',

    reorder_initiated: 'reorder',
    order_duplicated: 'reorder',
    order_created: 'activity_flow',
    order_deleted: 'dropdown_status',
    order_cancel: 'dropdown_status',
    on_hold: 'dropdown_status',
    resume_order: 'dropdown_status',

    quote_provided: 'dropdown_status',
    quote_rejected: 'dropdown_status',
    quote_updated: 'dropdown_status',
    quote_requested: 'dropdown_status',

    third_party_approved: 'third_party_approval',
    third_party_rejected: 'third_party_approval',

    // saveActivity() flow: action_type === order item status
    submitted_for_factory_review: 'activity_flow',
    factory_approved: 'activity_flow',
    factory_rejected: 'activity_flow',
    submitted_for_customer_review: 'sample_status',
    customer_approved: 'approval',
    customer_rejected: 'approval',
    production_files_uploaded: 'put_in_production',
    in_production: 'put_in_production',
    quality_control: 'activity_flow',
    shipped: 'shipping_details',
    completed: 'dropdown_status',

    sample_uploaded: 'sample_upload',
    sample_skipped: 'sample_status',
    sample_reuploaded: 'sample_rejected_reupload',
  };

  return direct[actionType] ?? 'activity_flow';
}

const TITLE_BY_ACTION: Record<string, string> = {
  factory_assigned: 'Factory assigned',
  factory_changed: 'Factory changed',
  production_date_changed: 'Production date changed',
  shipping_details_updated: 'Shipping details updated',
  tracking_link_updated: 'Tracking link updated',

  address_updated: 'Shipping address updated',
  po_number_updated: 'PO number updated',
  reference_number_updated: 'Reference number updated',
  roster_updated: 'Roster updated',

  order_admin_assigned: 'Order admin assigned',
  copied_order_without_admin: 'Order copied without Order Admin',
  urgency_updated: 'Urgency updated',
  dqc_status_updated: 'DQC status updated',

  techpack_edited: 'Tech pack edited',
  techpack_pdf_urls_updated: 'Tech pack PDF URLs updated',
  custom_pdf_uploaded: 'Custom PDF uploaded',
  custom_pdf_removed: 'Custom PDF removed',
  price_updated: 'Price updated',
  stripe_invoice_created: 'Stripe invoice created',
  stripe_invoice_number_updated: 'Stripe invoice number updated',

  internal_comment_added: 'Internal comment added',
  internal_comment_updated: 'Internal comment updated',
  internal_comment_deleted: 'Internal comment deleted',

  delivery_note_created: 'Delivery note created',
  delivery_note_updated: 'Delivery note updated',
  delivery_note_deleted: 'Delivery note deleted',
  delivery_box_updated: 'Delivery box updated',

  reorder_initiated: 'Reorder initiated',
  order_duplicated: 'Order duplicated',
  order_created: 'Order created',
  order_deleted: 'Order deleted',
  order_cancel: 'Order cancelled',
  on_hold: 'Order put on hold',
  resume_order: 'Order resumed',

  quote_provided: 'Quote provided',
  quote_rejected: 'Quote rejected',
  quote_updated: 'Quote updated',
  quote_requested: 'Quote requested',
  pending_for_factory_assignment: 'Pending for factory assignment',
  order_approve: 'Order approved',

  third_party_approved: 'Third-party approved',
  third_party_rejected: 'Third-party rejected',

  submitted_for_factory_review: 'Submitted for factory review',
  factory_approved: 'Factory approved',
  factory_rejected: 'Factory rejected',
  submitted_for_customer_review: 'Submitted for customer review',
  customer_approved: 'Customer approved',
  customer_rejected: 'Customer rejected',
  production_files_uploaded: 'Production files uploaded',
  in_production: 'In production',
  quality_control: 'Quality control',
  shipped: 'Shipped',
  completed: 'Completed',
};

function pickTitle(actionType: string, _meta: Meta): string {
  return TITLE_BY_ACTION[actionType] ?? humanizeStatus(actionType);
}

function pickActivityTypeBadge(actionType: string, meta: Meta): string {
  if (actionType === 'address_updated' && meta.activity_source === 'my_task') {
    return 'My Task';
  }
  if (
    actionType === 'submitted_for_customer_review' &&
    meta.activity_source === 'third_party_email'
  ) {
    return 'Third party';
  }
  if ((actionType === 'customer_approved' || actionType === 'customer_rejected') && isCustomerPortalSamples(meta)) {
    return 'Customer portal';
  }
  return TITLE_BY_ACTION[actionType] ?? humanizeStatus(actionType);
}

function pickDescription(actionType: string, meta: Meta): string {
  switch (actionType) {
    case 'po_number_updated': {
      const change = meta.po_number_updated ?? {};
      return `PO number changed from ${toDisplayString(change.from)} to ${toDisplayString(change.to)}.`;
    }
    case 'reference_number_updated': {
      const change = meta.reference_number_updated ?? {};
      return `Reference number changed from ${toDisplayString(change.from)} to ${toDisplayString(change.to)}.`;
    }
    case 'address_updated':
      return meta.activity_source === 'my_task'
        ? 'Shipping address updated from My Task.'
        : 'Shipping address updated from the details tab.';
    case 'roster_updated':
      return 'Roster information updated.';
    case 'order_created': {
      const orderNo = meta.order_no ? ` #${meta.order_no}` : '';
      const type = meta.order_type_detail ?? meta.order_type ?? 'Order';
      return `${type}${orderNo} created.`;
    }
    case 'order_duplicated':
      return `Order duplicated${meta.original_order_id ? ` from #${meta.original_order_id}` : ''}.`;
    case 'order_deleted':
      return `Order ${meta.order_no ? `#${meta.order_no} ` : ''}deleted.`;
    case 'order_cancel':
      return `Order ${meta.order_no ? `#${meta.order_no} ` : ''}cancelled${meta.from ? ` from ${meta.from}` : ''}.`;
    case 'on_hold':
      return `Status moved to On hold (was ${humanizeStatus(meta.previous_status)}).`;
    case 'resume_order':
      return `Hold removed; status restored to ${humanizeStatus(meta.restored_status)}.`;
    case 'production_date_changed': {
      const newDates = Array.isArray(meta.new_estimated_date)
        ? meta.new_estimated_date.join(', ')
        : toDisplayString(meta.new_estimated_date);
      return `Estimated shipping dates updated to ${newDates}.`;
    }
    case 'tracking_link_updated':
      return meta.previous_tracking_link
        ? 'Tracking link updated.'
        : 'Tracking link added.';
    case 'shipping_details_updated':
      return 'Shipping details updated.';
    case 'factory_assigned': {
      const count = Object.keys((meta.product_factory_ids as Meta) ?? {}).length;
      return `Factory assigned to ${count} product${count === 1 ? '' : 's'}.`;
    }
    case 'factory_changed': {
      const prev = optionalNonEmptyString(meta.previous_factory_name);
      const next = optionalNonEmptyString(meta.new_factory_name);
      if (prev && next) return `Factory changed from ${prev} to ${next}.`;
      if (prev) return `Factory changed from ${prev}.`;
      if (next) return `Factory changed to ${next}.`;
      return 'Factory changed.';
    }
    case 'order_admin_assigned': {
      const name =
        typeof meta.new_order_admin_name === 'string' ? meta.new_order_admin_name.trim() : '';
      const hasId = hasScalarValue(meta.new_order_admin_id);
      if (name && hasId) return `Order admin assigned to ${name} (ID ${meta.new_order_admin_id}).`;
      if (name) return `Order admin assigned to ${name}.`;
      if (hasId) return `Order admin assigned (user ID ${meta.new_order_admin_id}).`;
      return 'Order admin assigned.';
    }
    case 'copied_order_without_admin': {
      const reason = optionalNonEmptyString(meta.alert_reason);
      if (reason === 'original_admin_inactive') {
        return "Order copied without an Order Admin because the original order's admin is no longer active.";
      }
      if (reason === 'original_unassigned') {
        return 'Order copied without an Order Admin because the original order had none assigned.';
      }
      return 'Order copied without an Order Admin.';
    }
    case 'urgency_updated':
      return `Urgency changed from ${humanizeStatus(meta.previous_urgency)} to ${humanizeStatus(meta.new_urgency)}.`;
    case 'dqc_status_updated': {
      const flag = safeBool(meta.dqc_approved);
      return `DQC ${flag ? 'approved' : 'unapproved'}${meta.dqc_user_name ? ` by ${meta.dqc_user_name}` : ''}.`;
    }
    case 'techpack_edited': {
      const ids = Array.isArray(meta.order_item_ids_updated) ? meta.order_item_ids_updated.length : 0;
      return `Tech pack edited (${ids} item${ids === 1 ? '' : 's'}).`;
    }
    case 'techpack_pdf_urls_updated': {
      const merchantPending =
        safeBool(meta.merchant_generation_pending) ?? safeBool(meta.generation_pending);
      const factoryRows = Array.isArray(meta.factory_by_order_item) ? meta.factory_by_order_item : [];
      const anyFactoryPending = factoryRows.some((r: unknown) =>
        safeBool((r as Meta)?.generation_pending),
      );
      if (merchantPending || anyFactoryPending) {
        return 'Tech pack PDF URLs changed; PDF regeneration pending.';
      }
      const n = factoryRows.length;
      return n
        ? `Tech pack PDF URLs updated (${n} order line${n === 1 ? '' : 's'}).`
        : 'Tech pack PDF URLs updated.';
    }
    case 'custom_pdf_uploaded': {
      const names = Array.isArray(meta.uploaded_file_names) ? meta.uploaded_file_names : [];
      if (!names.length) return 'Custom PDF uploaded.';
      return `Custom PDF uploaded: ${names.join(', ')}.`;
    }
    case 'custom_pdf_removed':
      return `Custom PDF removed${meta.removed_file_name ? `: ${meta.removed_file_name}` : ''}.`;
    case 'price_updated':
      return `Price updated (${meta.scope === 'order' ? 'order-level' : 'item-level'}).`;
    case 'stripe_invoice_created': {
      const num = optionalNonEmptyString(meta.stripe_invoice_number) ??
        optionalNonEmptyString(meta.new?.stripe_invoice_number);
      const status = optionalNonEmptyString(meta.stripe_invoice_status) ??
        optionalNonEmptyString(meta.new?.stripe_invoice_status);
      if (num && status) return `Stripe invoice ${num} created with status ${status}.`;
      if (num) return `Stripe invoice ${num} created.`;
      return 'Stripe invoice created.';
    }
    case 'stripe_invoice_number_updated': {
      const prevNum = optionalNonEmptyString(meta.previous?.stripe_invoice_number);
      const nextNum = optionalNonEmptyString(meta.new?.stripe_invoice_number) ??
        optionalNonEmptyString(meta.stripe_invoice_number);
      if (prevNum && nextNum) return `Stripe invoice number updated from ${prevNum} to ${nextNum}.`;
      if (nextNum) return `Stripe invoice number updated to ${nextNum}.`;
      return 'Stripe invoice number updated.';
    }
    case 'internal_comment_added':
      return 'Internal comment added.';
    case 'internal_comment_updated':
      return 'Internal comment edited.';
    case 'internal_comment_deleted':
      return 'Internal comment deleted.';
    case 'delivery_note_created': {
      const ids = Array.isArray(meta.delivery_note_ids) ? meta.delivery_note_ids : [];
      if (ids.length > 1) {
        return `Delivery notes created (${ids.length} notes).`;
      }
      if (ids.length === 1) {
        return `Delivery note created (#${ids[0]}).`;
      }
      return `Delivery note created${meta.delivery_note_id ? ` (#${meta.delivery_note_id})` : ''}.`;
    }
    case 'delivery_note_updated':
      return meta.was_split ? 'Delivery note updated and split.' : 'Delivery note updated.';
    case 'delivery_note_deleted':
      return `Delivery note deleted${meta.delivery_note_id ? ` (#${meta.delivery_note_id})` : ''}.`;
    case 'delivery_box_updated': {
      const sub = meta.sub_action ?? 'updated';
      return `Delivery box ${humanizeStatus(sub).toLowerCase()}.`;
    }
    case 'reorder_initiated':
      return `Reorder initiated${meta.reorder_order_no ? ` (#${meta.reorder_order_no})` : ''}.`;
    case 'third_party_approved':
      return `Third-party approved${meta.third_party_email ? ` by ${meta.third_party_email}` : ''}.`;
    case 'third_party_rejected':
      return `Third-party rejected${meta.third_party_email ? ` by ${meta.third_party_email}` : ''}.`;
    case 'submitted_for_customer_review':
      return meta.activity_source === 'third_party_email'
        ? `Sent for third-party approval${meta.third_party_email ? ` to ${meta.third_party_email}` : ''}.`
        : 'Samples submitted for customer review.';
    case 'customer_approved':
      return isCustomerPortalSamples(meta) ? 'Customer approved samples from portal.' : 'Customer approved.';
    case 'customer_rejected':
      return isCustomerPortalSamples(meta) ? 'Customer rejected samples from portal.' : 'Customer rejected.';
    case 'production_files_uploaded':
      return 'Production files uploaded.';
    case 'in_production':
      return 'Order moved to production.';
    case 'shipped':
      return 'Order shipped.';
    case 'completed':
      return 'Order completed.';
    case 'submitted_for_factory_review':
      return 'Submitted for factory review.';
    case 'factory_approved':
      return 'Factory approved.';
    case 'factory_rejected':
      return 'Factory rejected.';
    default:
      return humanizeStatus(actionType) + '.';
  }
}

function resolveUserRole(row: BackendOrderActivityLog, meta: Meta): string {
  if (typeof meta.user_role === 'string' && meta.user_role.trim().length > 0) {
    return meta.user_role;
  }
  const raw = row.user_role;
  if (typeof raw === 'string' && raw.trim().length > 0) return raw;
  if (Array.isArray(raw) && raw.length > 0) {
    const first = raw[0] ?? {};
    const label = typeof first.label === 'string' ? first.label.trim() : '';
    const name = typeof first.name === 'string' ? first.name.trim() : '';
    return label || name || 'System';
  }
  return 'System';
}

function buildUser(row: BackendOrderActivityLog, meta: Meta) {
  const name = row.user?.name?.trim() || (typeof meta.customer_name === 'string' ? meta.customer_name : '') || 'System';
  const role = resolveUserRole(row, meta);
  return { name, role };
}

/* ----------------------- detail-pane builders --------------------- */

function appendShippingDetailsFieldRows(rows: OrderActivityFieldChange[], sd: Meta | null | undefined): void {
  if (!sd || typeof sd !== 'object') return;
  if (sd.carrier_name !== undefined) {
    rows.push({
      field_label: 'Carrier',
      previous_value: '—',
      new_value: toDisplayString(sd.carrier_name),
    });
  }
  if (sd.tracking_number !== undefined) {
    rows.push({
      field_label: 'Tracking number',
      previous_value: '—',
      new_value: toDisplayString(sd.tracking_number),
    });
  }
  if (sd.tracking_link !== undefined) {
    rows.push({
      field_label: 'Tracking link',
      previous_value: '—',
      new_value: toDisplayString(sd.tracking_link),
    });
  }
}

function buildFieldChanges(actionType: string, meta: Meta): OrderActivityFieldChange[] | undefined {
  const rows: OrderActivityFieldChange[] = [];

  if (actionType === 'po_number_updated') {
    const c = meta.po_number_updated ?? {};
    rows.push({
      field_label: 'PO number',
      previous_value: toDisplayString(c.from),
      new_value: toDisplayString(c.to),
    });
  }
  if (actionType === 'reference_number_updated') {
    const c = meta.reference_number_updated ?? {};
    rows.push({
      field_label: 'Reference number',
      previous_value: toDisplayString(c.from),
      new_value: toDisplayString(c.to),
    });
  }
  if (actionType === 'roster_updated') {
    const c = meta.roster_updated ?? {};
    rows.push({
      field_label: 'Roster',
      previous_value: toDisplayString(c.old),
      new_value: toDisplayString(c.new),
    });
  }
  if (actionType === 'production_date_changed') {
    const prev = Array.isArray(meta.previous_production_estimate_date)
      ? meta.previous_production_estimate_date.join(', ')
      : toDisplayString(meta.previous_production_estimate_date);
    const next = Array.isArray(meta.new_estimated_date)
      ? meta.new_estimated_date.join(', ')
      : toDisplayString(meta.new_estimated_date);
    rows.push({
      field_label: 'Estimated shipping dates',
      previous_value: prev || '—',
      new_value: next || '—',
    });
    if (meta.action_taken) {
      rows.push({
        field_label: 'Notification',
        previous_value: '—',
        new_value: String(meta.action_taken),
      });
    }
  }
  if (actionType === 'urgency_updated') {
    rows.push({
      field_label: 'Urgency',
      previous_value: humanizeStatus(meta.previous_urgency),
      new_value: humanizeStatus(meta.new_urgency),
    });
  }
  if (actionType === 'dqc_status_updated') {
    rows.push({
      field_label: 'DQC approved',
      previous_value: toDisplayString(safeBool(meta.previous_mark_dqc) ?? meta.previous_mark_dqc),
      new_value: toDisplayString(safeBool(meta.dqc_approved) ?? meta.dqc_approved),
    });
  }
  if (actionType === 'order_admin_assigned') {
    rows.push({
      field_label: 'Order admin',
      previous_value: formatOrderAdminCell(meta, 'previous'),
      new_value: formatOrderAdminCell(meta, 'new'),
    });
  }
  if (actionType === 'shipping_details_updated' && meta.shipping_details) {
    appendShippingDetailsFieldRows(rows, meta.shipping_details as Meta);
  }
  if (actionType === 'shipped' && meta.shipping_details) {
    appendShippingDetailsFieldRows(rows, meta.shipping_details as Meta);
  }
  if (actionType === 'stripe_invoice_created' || actionType === 'stripe_invoice_number_updated') {
    const previous =
      meta.previous && typeof meta.previous === 'object' && !Array.isArray(meta.previous)
        ? (meta.previous as Meta)
        : {};
    const next =
      meta.new && typeof meta.new === 'object' && !Array.isArray(meta.new)
        ? (meta.new as Meta)
        : {};
    const previousInfo =
      previous.stripe_invoice_info && typeof previous.stripe_invoice_info === 'object' && !Array.isArray(previous.stripe_invoice_info)
        ? (previous.stripe_invoice_info as Meta)
        : {};
    const nextInfo =
      next.stripe_invoice_info && typeof next.stripe_invoice_info === 'object' && !Array.isArray(next.stripe_invoice_info)
        ? (next.stripe_invoice_info as Meta)
        : {};

    rows.push({
      field_label: 'Stripe invoice ID',
      previous_value: toDisplayString(previous.stripe_invoice_id),
      new_value: toDisplayString(next.stripe_invoice_id ?? meta.stripe_invoice_id),
    });
    rows.push({
      field_label: 'Stripe invoice number',
      previous_value: toDisplayString(previous.stripe_invoice_number),
      new_value: toDisplayString(next.stripe_invoice_number ?? meta.stripe_invoice_number),
    });
    rows.push({
      field_label: 'Stripe invoice status',
      previous_value: toDisplayString(previous.stripe_invoice_status),
      new_value: toDisplayString(next.stripe_invoice_status ?? meta.stripe_invoice_status),
    });
    rows.push({
      field_label: 'Hosted invoice URL',
      previous_value: toDisplayString(previousInfo.hosted_invoice_url),
      new_value: toDisplayString(nextInfo.hosted_invoice_url ?? meta.hosted_invoice_url),
    });
    rows.push({
      field_label: 'Invoice PDF',
      previous_value: toDisplayString(previousInfo.invoice_pdf),
      new_value: toDisplayString(nextInfo.invoice_pdf ?? meta.invoice_pdf),
    });

    const prevComment = previousInfo.invoice_comment;
    const nextComment = nextInfo.invoice_comment;
    if (prevComment !== undefined || nextComment !== undefined) {
      rows.push({
        field_label: 'Invoice comment',
        previous_value: toDisplayString(prevComment),
        new_value: toDisplayString(nextComment),
      });
    }
  }

  return rows.length ? rows : undefined;
}

function buildPdfChanges(actionType: string, meta: Meta): OrderActivityPdfChange[] | undefined {
  if (actionType === 'custom_pdf_uploaded') {
    const names: string[] = Array.isArray(meta.uploaded_file_names) ? meta.uploaded_file_names : [];
    const oldLinks: string[] = Array.isArray(meta.old_pdf_links) ? meta.old_pdf_links : [];
    const newLinks: string[] = Array.isArray(meta.new_pdf_links) ? meta.new_pdf_links : [];
    if (!names.length && !oldLinks.length && !newLinks.length) return undefined;
    const rows: OrderActivityPdfChange[] = [];
    names.forEach((name, idx) => {
      const nextRaw = newLinks[idx] ?? (idx === 0 ? meta.new_pdf_link : undefined);
      rows.push({
        label: name,
        previous_url: s3Url(oldLinks[idx]) ?? '—',
        new_url: (typeof nextRaw === 'string' ? s3Url(nextRaw) : undefined) ?? '—',
      });
    });
    return rows.length ? rows : undefined;
  }
  if (actionType === 'custom_pdf_removed') {
    return [
      {
        label: typeof meta.removed_file_name === 'string' ? meta.removed_file_name : 'Removed PDF',
        previous_url: s3Url(meta.old_pdf_link as string | undefined) ?? '—',
        new_url: '—',
      },
    ];
  }
  return undefined;
}

function buildFactory(actionType: string, meta: Meta): OrderActivityFactoryChange | undefined {
  if (actionType !== 'factory_changed') return undefined;

  const previous_name =
    optionalNonEmptyString(meta.previous_factory_name) ??
    (hasScalarValue(meta.previous_factory_name) ? String(meta.previous_factory_name) : undefined) ??
    '—';

  const nextFromMeta =
    optionalNonEmptyString(meta.new_factory_name) ??
    (hasScalarValue(meta.new_factory_name) ? String(meta.new_factory_name) : undefined);

  const hasAssignments =
    Array.isArray(meta.product_factory_assignments) && meta.product_factory_assignments.length > 0;

  const new_name = nextFromMeta ?? (hasAssignments ? '(see assignments)' : '—');

  return {
    previous_name,
    new_name,
  };
}

function buildTrackingLink(actionType: string, meta: Meta): OrderActivityTrackingLink | undefined {
  if (actionType !== 'tracking_link_updated') return undefined;
  const action: 'added' | 'edited' = meta.previous_tracking_link ? 'edited' : 'added';
  return {
    action,
    previous_url: typeof meta.previous_tracking_link === 'string' ? meta.previous_tracking_link : undefined,
    new_url: typeof meta.new_tracking_link === 'string' ? meta.new_tracking_link : undefined,
  };
}

const DELIVERY_BOX_LABEL: Record<string, string> = {
  box_created: 'Box created',
  box_updated: 'Box updated',
  box_deleted: 'Box deleted',
  note_line_created: 'Note line added',
};

function parseNewCustomBoxes(meta: Meta): OrderActivityDeliveryBox['new_custom_boxes'] {
  const raw = meta.new_custom_boxes;
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  const rows: NonNullable<OrderActivityDeliveryBox['new_custom_boxes']> = [];
  for (const item of raw) {
    const d = item?.dimensions ?? {};
    rows.push({
      label: typeof item?.label === 'string' ? item.label : toDisplayString(item?.label),
      dimensions: {
        width: d.width ?? '—',
        height: d.height ?? '—',
        length: d.length ?? '—',
        weight: d.weight ?? '—',
      },
    });
  }
  return rows.length ? rows : undefined;
}

function parseDeliveryNotesBoxes(meta: Meta): OrderActivityDeliveryBox['delivery_notes_boxes'] {
  const raw = meta.delivery_notes_boxes;
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  const rows: NonNullable<OrderActivityDeliveryBox['delivery_notes_boxes']> = [];
  for (const item of raw) {
    const id = Number(item?.delivery_note_id);
    rows.push({
      box_label: typeof item?.box_label === 'string' ? item.box_label : toDisplayString(item?.box_label),
      delivery_note_id: Number.isFinite(id) ? id : 0,
      packaging_box_id: item?.packaging_box_id != null ? String(item.packaging_box_id) : '—',
      packaging_box_label:
        typeof item?.packaging_box_label === 'string'
          ? item.packaging_box_label
          : toDisplayString(item?.packaging_box_label),
    });
  }
  return rows.length ? rows : undefined;
}

function buildDeliveryBox(actionType: string, meta: Meta): OrderActivityDeliveryBox | undefined {
  if (actionType === 'delivery_box_updated') {
    const sub = typeof meta.sub_action === 'string' ? meta.sub_action : 'updated';
    return {
      box_id: (meta.delivery_box_id as string | number) ?? '—',
      action_label: DELIVERY_BOX_LABEL[sub] ?? humanizeStatus(sub),
    };
  }
  if (actionType === 'delivery_note_created') {
    const rawIds = Array.isArray(meta.delivery_note_ids) ? meta.delivery_note_ids : [];
    const delivery_note_ids = rawIds.length
      ? rawIds.map((n: unknown) => Number(n)).filter((n) => Number.isFinite(n))
      : meta.delivery_note_id !== undefined && meta.delivery_note_id !== null
        ? [Number(meta.delivery_note_id)]
        : undefined;

    const primaryId =
      delivery_note_ids?.[0] ??
      (meta.delivery_note_id !== undefined && meta.delivery_note_id !== null
        ? Number(meta.delivery_note_id)
        : undefined);

    const box_labels = Array.isArray(meta.box_labels)
      ? meta.box_labels.map((x: unknown) => String(x))
      : undefined;

    const new_custom_boxes = parseNewCustomBoxes(meta);
    const delivery_notes_boxes = parseDeliveryNotesBoxes(meta);

    const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);

    return {
      box_id: primaryId ?? '—',
      box_name: box_labels?.length ? box_labels.join(', ') : undefined,
      action_label: 'Delivery note created',
      activity_source: typeof meta.activity_source === 'string' ? meta.activity_source : undefined,
      box_labels,
      total_items_qty: num(meta.total_items_qty),
      order_line_rows_count: num(meta.order_line_rows_count),
      not_included_items_qty: num(meta.not_included_items_qty),
      items_qty_to_ship_total: num(meta.items_qty_to_ship_total),
      delivery_note_ids,
      new_custom_boxes,
      delivery_notes_boxes,
    };
  }
  if (actionType === 'delivery_note_updated') {
    const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
    const rawCreated = Array.isArray(meta.created_delivery_note_ids) ? meta.created_delivery_note_ids : [];
    const createdIds = rawCreated.map((n: unknown) => Number(n)).filter((n) => Number.isFinite(n));

    const box_labels = Array.isArray(meta.box_labels)
      ? meta.box_labels.map((x: unknown) => String(x))
      : undefined;

    const new_custom_boxes = parseNewCustomBoxes(meta);
    const delivery_notes_boxes = parseDeliveryNotesBoxes(meta);

    const deletedRaw = meta.deleted_delivery_note_id;
    const primaryBoxId =
      deletedRaw !== undefined && deletedRaw !== null
        ? deletedRaw
        : (meta.delivery_note_id as string | number | undefined) ?? '—';

    return {
      box_id: primaryBoxId,
      action_label: meta.was_split ? 'Delivery note updated and split' : 'Delivery note updated',
      activity_source: typeof meta.activity_source === 'string' ? meta.activity_source : undefined,
      box_labels,
      total_items_qty: num(meta.total_items_qty),
      order_line_rows_count: num(meta.order_line_rows_count),
      not_included_items_qty: num(meta.not_included_items_qty),
      items_qty_to_ship_total: num(meta.items_qty_to_ship_total),
      delivery_note_ids: createdIds.length ? createdIds : undefined,
      created_delivery_note_ids: createdIds.length ? createdIds : undefined,
      deleted_delivery_note_id:
        deletedRaw !== undefined && deletedRaw !== null ? Number(deletedRaw) : undefined,
      was_split: typeof meta.was_split === 'boolean' ? meta.was_split : undefined,
      new_custom_boxes,
      delivery_notes_boxes,
    };
  }
  if (actionType === 'delivery_note_deleted') {
    return {
      box_id: (meta.delivery_note_id as string | number) ?? '—',
      action_label: 'Delivery note deleted',
    };
  }
  return undefined;
}

function buildComment(actionType: string, meta: Meta): OrderActivityComment | undefined {
  if (actionType === 'internal_comment_added') {
    return { action: 'added', excerpt: typeof meta.comment_preview === 'string' ? meta.comment_preview : undefined };
  }
  if (actionType === 'internal_comment_updated') {
    return { action: 'edited', excerpt: typeof meta.comment_preview === 'string' ? meta.comment_preview : undefined };
  }
  if (actionType === 'internal_comment_deleted') {
    return { action: 'deleted' };
  }
  return undefined;
}

function buildReorder(actionType: string, meta: Meta): OrderActivityReorderInfo | undefined {
  if (actionType === 'reorder_initiated') {
    return {
      original_order_id: Number(meta.original_order_id ?? 0) || 0,
      original_order_no: toDisplayString(meta.reorder_order_no),
    };
  }
  if (actionType === 'order_duplicated') {
    return {
      original_order_id: Number(meta.original_order_id ?? 0) || 0,
      original_order_no: toDisplayString(meta.order_no),
    };
  }
  return undefined;
}

function buildShippingSnapshot(actionType: string, meta: Meta): OrderActivityShippingSnapshot | undefined {
  if (actionType !== 'address_updated') return undefined;
  const oldAddr = meta.old_address ?? meta.address_updated?.old;
  const newAddr = meta.new_address ?? meta.address_updated?.new;
  if (!oldAddr && !newAddr) return undefined;
  return {
    previous_summary: formatAddress(oldAddr),
    new_summary: formatAddress(newAddr),
  };
}

function safeFiniteNum(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function normalizePriceSnapshot(raw: Record<string, unknown>): OrderActivityPriceBreakdownSnapshot {
  return {
    discount: safeFiniteNum(raw.discount),
    is_fixed: safeBool(raw.is_fixed),
    shipping: safeFiniteNum(raw.shipping),
    fixed_value: safeFiniteNum(raw.fixed_value),
    total_price: safeFiniteNum(raw.total_price),
    total_quantity: safeFiniteNum(raw.total_quantity),
    order_item_final_price: safeFiniteNum(raw.order_item_final_price),
    order_final_price: safeFiniteNum(raw.order_final_price),
    total_final_price: safeFiniteNum(raw.total_final_price),
  };
}

function snapshotHasAnyField(s: OrderActivityPriceBreakdownSnapshot): boolean {
  return Object.values(s).some((v) => v !== undefined);
}

function buildPriceUpdateDetail(actionType: string, meta: Meta): OrderActivityPriceUpdateDetail | undefined {
  if (actionType !== 'price_updated') return undefined;
  const prevRaw =
    meta.previous_price_info ??
    meta.previous_price_summary ??
    meta.previous_total_price;
  const nextRaw = meta.updated_price_value ?? meta.new_price_summary;
  let previous: OrderActivityPriceBreakdownSnapshot | undefined;
  let updated: OrderActivityPriceBreakdownSnapshot | undefined;
  if (prevRaw && typeof prevRaw === 'object' && !Array.isArray(prevRaw)) {
    previous = normalizePriceSnapshot(prevRaw as Record<string, unknown>);
  }
  if (nextRaw && typeof nextRaw === 'object' && !Array.isArray(nextRaw)) {
    updated = normalizePriceSnapshot(nextRaw as Record<string, unknown>);
  }
  if (!snapshotHasAnyField(previous ?? {}) && !snapshotHasAnyField(updated ?? {})) return undefined;
  return {
    previous: previous ?? {},
    updated: updated ?? {},
  };
}

function buildStateEntries(
  actionType: string,
  meta: Meta,
): { previous?: OrderActivityStateEntry[]; next?: OrderActivityStateEntry[] } {
  if (actionType === 'on_hold') {
    return {
      previous: [{ label: 'Status', value: humanizeStatus(meta.previous_status) }],
      next: [{ label: 'Status', value: 'On hold' }],
    };
  }
  if (actionType === 'resume_order') {
    return {
      previous: [{ label: 'Status', value: 'On hold' }],
      next: [{ label: 'Status', value: humanizeStatus(meta.restored_status) }],
    };
  }
  // For status moves that come through saveActivity (action_type === order item status)
  if (
    [
      'pending_for_factory_assignment',
      'order_approve',
      'quote_requested',
      'quote_updated',
      'submitted_for_factory_review',
      'factory_approved',
      'factory_rejected',
      'submitted_for_customer_review',
      'customer_approved',
      'customer_rejected',
      'production_files_uploaded',
      'in_production',
      'quality_control',
      'shipped',
      'completed',
    ].includes(actionType) &&
    meta.previous_status
  ) {
    return {
      previous: [{ label: 'Status', value: humanizeStatus(meta.previous_status) }],
      next: [{ label: 'Status', value: humanizeStatus(actionType) }],
    };
  }
  if (actionType === 'price_updated') {
    const prevRaw =
      meta.previous_price_info ??
      meta.previous_price_summary ??
      meta.previous_total_price;
    const nextRaw = meta.updated_price_value ?? meta.new_price_summary;
    const prevObj = prevRaw && typeof prevRaw === 'object' && !Array.isArray(prevRaw);
    const nextObj = nextRaw && typeof nextRaw === 'object' && !Array.isArray(nextRaw);
    if (prevObj || nextObj) {
      return {};
    }
    if (prevRaw !== undefined || nextRaw !== undefined) {
      return {
        previous: prevRaw !== undefined ? [{ label: 'Price info', value: toDisplayString(prevRaw) }] : undefined,
        next: nextRaw !== undefined ? [{ label: 'Price info', value: toDisplayString(nextRaw) }] : undefined,
      };
    }
  }
  return {};
}

/** saveActivity / order-item-status rows often share these meta keys */
const WORKFLOW_META_ACTION_TYPES = new Set<string>([
  'pending_for_factory_assignment',
  'quote_requested',
  'quote_updated',
  'quote_provided',
  'quote_rejected',
  'submitted_for_factory_review',
  'factory_approved',
  'factory_rejected',
  'submitted_for_customer_review',
  'customer_approved',
  'customer_rejected',
  'production_files_uploaded',
  'in_production',
  'quality_control',
  'shipped',
  'completed',
  'order_approve',
  'sample_uploaded',
  'sample_skipped',
  'sample_reuploaded',
]);

function pushWorkflowCommonLines(
  push: (label: string, value: any) => void,
  actionType: string,
  meta: Meta,
): void {
  if (!WORKFLOW_META_ACTION_TYPES.has(actionType)) return;
  if (hasScalarValue(meta.reference_number)) push('Reference #', meta.reference_number);
  const notes = optionalNonEmptyString(meta.additional_notes);
  const reasonStr = optionalNonEmptyString(meta.reason);
  if (notes && notes !== reasonStr) push('Additional notes', notes);
  if (hasScalarValue(meta.order_id)) push('Order ID', meta.order_id);
  if (hasScalarValue(meta.order_no)) push('Order #', meta.order_no);
  const ot = meta.order_type_detail ?? meta.order_type;
  if (hasScalarValue(ot)) push('Order type', ot);
}

function buildMetadataLines(
  actionType: string,
  meta: Meta,
  row?: BackendOrderActivityLog,
): OrderActivityMetadataLine[] | undefined {
  const lines: OrderActivityMetadataLine[] = [];

  const push = (label: string, value: any) => {
    if (value === null || value === undefined || value === '') return;
    lines.push({ label, value: toDisplayString(value) });
  };

  pushWorkflowCommonLines(push, actionType, meta);

  if (actionType === 'order_approve') {
    const app = formatIdList(meta.samples_approved);
    const skip = formatIdList(meta.samples_skipped);
    if (app) push('Samples approved (IDs)', app);
    if (skip) push('Samples skipped (IDs)', skip);
  }

  if (actionType === 'quote_rejected' && hasScalarValue(meta.customer_id)) {
    push('Customer ID', meta.customer_id);
  }

  if (actionType === 'order_created') {
    push('Order #', meta.order_no);
    push('Order type', meta.order_type_detail ?? meta.order_type);
    push('Customer reference', meta.customer_reference_no);
    if (meta.customer_shop_id) push('Shop ID', meta.customer_shop_id);
    const ob = safeBool(meta.on_behalf);
    if (ob !== undefined) push('On behalf', ob ? 'Yes' : 'No');
    const iq = safeBool(meta.is_quote_order);
    if (iq !== undefined) push('Quote order', iq ? 'Yes' : 'No');
  }

  if (actionType === 'delivery_note_created') {
    if (meta.shipping_address && typeof meta.shipping_address === 'object') {
      const addrText = formatAddress(meta.shipping_address);
      if (addrText && addrText !== '—') {
        lines.push({ label: 'Delivery address', value: addrText });
      }
    }
  }

  if (actionType === 'delivery_note_updated') {
    const created = formatIdList(meta.created_delivery_note_ids);
    if (created) push('Created delivery note IDs', created);
    push('Order line rows', meta.order_line_rows_count);
    push('Total items qty', meta.total_items_qty);
    push('Items qty to ship (total)', meta.items_qty_to_ship_total);
    push('Not included items qty', meta.not_included_items_qty);
    if (meta.was_split === true) push('Split', 'Yes');
    if (hasScalarValue(meta.deleted_delivery_note_id)) {
      push('Deleted delivery note ID', meta.deleted_delivery_note_id);
    }
  }

  if (actionType === 'quality_control') {
    if (typeof meta.sub_action === 'string' && meta.sub_action.trim()) {
      push('Sub action', humanizeStatus(meta.sub_action.trim()));
    }
    push('QA status', meta.qa_status);
    if (hasScalarValue(meta.factory_id)) push('Factory ID', meta.factory_id);
    if (hasScalarValue(meta.order_id)) push('Order ID', meta.order_id);
    push('Conclusion', meta.conclusion);
    const pdf = optionalNonEmptyString(meta.pdf_link);
    if (pdf) push('QA report URL', pdf);
  }

  if (actionType === 'address_updated' && meta.activity_source === 'my_task') {
    if (hasScalarValue(meta.user_task_id)) push('User task ID', meta.user_task_id);
  }

  if (actionType === 'reorder_initiated') {
    push('Action', meta.action);
    push('New order #', meta.reorder_order_no);
  }

  if (actionType === 'order_admin_assigned') {
    if (typeof meta.activity_source === 'string' && meta.activity_source.trim()) {
      push('Activity source', humanizeActivitySource(meta.activity_source.trim()));
    }
    const secondary = meta.secondary_administrator_ids;
    if (Array.isArray(secondary) && secondary.length > 0) {
      const text = secondary
        .map((x) =>
          x !== null && x !== undefined && typeof x !== 'object'
            ? String(x)
            : JSON.stringify(x),
        )
        .join(', ');
      push('Secondary administrators', text);
    }
  }

  if (actionType === 'copied_order_without_admin') {
    const reason = optionalNonEmptyString(meta.alert_reason);
    if (reason) push('Reason', humanizeStatus(reason));
    const alertSent = safeBool(meta.alert_sent);
    if (alertSent !== undefined) push('Email alert sent', alertSent ? 'Yes' : 'No');
    if (hasScalarValue(meta.original_order_id)) push('Original order ID', meta.original_order_id);
  }

  if (actionType === 'factory_assigned') {
    if (typeof meta.activity_source === 'string' && meta.activity_source.trim()) {
      push('Activity source', humanizeActivitySource(meta.activity_source.trim()));
    }

    const factoryNames = formatIdList(meta.new_factory_names);
    if (factoryNames) {
      push('Factories', factoryNames);
    } else if (optionalNonEmptyString(meta.new_factory_name)) {
      push('Factories', meta.new_factory_name);
    }

    if (optionalNonEmptyString(meta.order_admin_name)) {
      push('Order admin', meta.order_admin_name);
    }

    const secondaryNames =
      formatIdList(meta.secondary_administrator_names) ??
      formatIdList(meta.secondary_order_admin_names);
    if (secondaryNames) {
      push('Secondary administrators', secondaryNames);
    }
  }

  if (actionType === 'factory_changed') {
    if (typeof meta.activity_source === 'string' && meta.activity_source.trim()) {
      push('Activity source', humanizeActivitySource(meta.activity_source.trim()));
    }
    if (hasScalarValue(meta.source_order_item_id)) {
      push('Source order item', meta.source_order_item_id);
    }
    if (hasScalarValue(meta.previous_factory_id)) {
      push('Previous factory ID', meta.previous_factory_id);
    }
    const pa = meta.product_factory_assignments;
    if (Array.isArray(pa) && pa.length > 0) {
      push('Assignment entries', pa.length);
      if (pa.length <= 8) {
        try {
          push('Assignments (JSON)', JSON.stringify(pa));
        } catch {
          push('Assignments', String(pa.length));
        }
      }
    }
  }

  if (actionType === 'dqc_status_updated') {
    push('Marked by', meta.dqc_user_name);
    push('Marked at', meta.marked_at);
  }

  if (actionType === 'on_hold' || actionType === 'resume_order') {
    push('Order #', meta.order_no);
  }

  if (actionType === 'tracking_link_updated') {
    push('Carrier', meta.shipping_company);
    push('Tracking #', meta.tracking_no);
  }

  if (actionType === 'third_party_approved' || actionType === 'third_party_rejected') {
    push('Third-party email', meta.third_party_email);
    push('Approval status', meta.approval_status);
    if (Array.isArray(meta.approval_details)) {
      push('Decisions', meta.approval_details.length);
    }
  }

  if (actionType === 'submitted_for_customer_review' && meta.activity_source === 'third_party_email') {
    push('Third-party email', meta.third_party_email);
    push('Sub action', meta.sub_action);
  }

  if (actionType === 'price_updated') {
    push('Scope', meta.scope);
    if (hasScalarValue(meta.order_id)) push('Order ID', meta.order_id);
    if (hasScalarValue(meta.user_role)) push('User role', meta.user_role);
    const orderType = meta.order_type_detail ?? meta.order_type;
    if (hasScalarValue(orderType)) push('Order type', orderType);
    if (typeof meta.activity_source === 'string' && meta.activity_source.trim()) {
      push('Activity source', humanizeActivitySource(meta.activity_source.trim()));
    }
    const orderTypeChanged = safeBool(meta.order_type_changed);
    if (orderTypeChanged !== undefined) {
      push('Order type changed', orderTypeChanged ? 'Yes' : 'No');
    }
    const orderTypePreviousDetail =
      optionalNonEmptyString(meta.previous_order_type_detail) ??
      optionalNonEmptyString(meta.order_type_previous?.detail);
    if (orderTypePreviousDetail) {
      push('Previous order type', orderTypePreviousDetail);
    }
    const orderTypeNewDetail =
      optionalNonEmptyString(meta.new_order_type_detail) ??
      optionalNonEmptyString(meta.order_type_new?.detail);
    if (orderTypeNewDetail) {
      push('New order type', orderTypeNewDetail);
    }
    const sc = typeof meta.scope === 'string' ? meta.scope.trim().toLowerCase() : '';
    if (sc === 'order_item' && row && hasScalarValue(row.related_id)) {
      push('Order item ID', row.related_id);
    }
    if (hasScalarValue(meta.previous_factory_product_count)) {
      push('Factory products (before)', meta.previous_factory_product_count);
    }
    if (hasScalarValue(meta.new_factory_product_count)) {
      push('Factory products (after)', meta.new_factory_product_count);
    }
    const fb = safeBool(meta.factory_products_added_from_modal);
    if (fb !== undefined) push('Factory products added from modal', fb ? 'Yes' : 'No');
    const addedIds = formatIdList(meta.added_factory_product_ids);
    if (addedIds) push('Added factory product IDs', addedIds);
  }

  if (actionType === 'stripe_invoice_created' || actionType === 'stripe_invoice_number_updated') {
    push('Scope', meta.scope);
    if (hasScalarValue(meta.order_id)) push('Order ID', meta.order_id);
    if (hasScalarValue(meta.order_no)) push('Order #', meta.order_no);
    if (hasScalarValue(meta.user_role)) push('User role', meta.user_role);
    if (typeof meta.activity_source === 'string' && meta.activity_source.trim()) {
      push('Activity source', humanizeActivitySource(meta.activity_source.trim()));
    }

    const created = safeBool(meta.invoice_number_created);
    if (created !== undefined) {
      push('Invoice number created', created ? 'Yes' : 'No');
    }
  }

  if (actionType === 'techpack_edited') {
    if (hasScalarValue(meta.order_id)) push('Order ID', meta.order_id);
    if (typeof meta.activity_source === 'string' && meta.activity_source.trim()) {
      push('Activity source', humanizeActivitySource(meta.activity_source.trim()));
    }
    if (Array.isArray(meta.order_item_ids_updated)) {
      push('Items updated', meta.order_item_ids_updated.length);
    }
  }

  if (actionType === 'techpack_pdf_urls_updated') {
    if (hasScalarValue(meta.order_id)) push('Order ID', meta.order_id);
    if (typeof meta.activity_source === 'string' && meta.activity_source.trim()) {
      push('Activity source', humanizeActivitySource(meta.activity_source.trim()));
    }
    const cn = meta.comparison_note;
    if (typeof cn === 'string' && cn.trim()) {
      push('Comparison', humanizeStatus(cn.trim()));
    }
  }

  if (actionType === 'customer_approved' || actionType === 'customer_rejected') {
    if (isCustomerPortalSamples(meta)) {
      push('Customer name', meta.customer_name);
      if (Array.isArray(meta.samples_by_status)) {
        push('Samples reviewed', meta.samples_by_status.length);
      }
    }
  }

  return lines.length ? lines : undefined;
}

/** Normalize storage paths for dedupe / comparison (slashes, trim). */
function normalizeStoragePath(p: string): string {
  return p.replace(/\\/g, '/').replace(/^\/+/, '').trim();
}

/** Images explicitly attached to this activity (not historical product gallery). */
function imagesFromUploadedPaths(meta: Meta): OrderActivityImageRef[] {
  const uploaded = safeNonEmptyArray<string>(meta.uploaded_image_paths);
  if (!uploaded?.length) return [];
  const seen = new Set<string>();
  const out: OrderActivityImageRef[] = [];
  for (const raw of uploaded) {
    const p = String(raw).trim();
    const norm = normalizeStoragePath(p);
    if (!norm || seen.has(norm)) continue;
    seen.add(norm);
    const href = s3Url(p) ?? p;
    const tail = p.split(/[/\\]/).pop() ?? 'Image';
    out.push({ url: href, label: tail });
  }
  return out;
}

/** Per-item files for this activity only (excludes product_info front/back — those can be stale). */
function extractActivityFilesImagesOnly(item: Meta): OrderActivityImageRef[] {
  const seen = new Set<string>();
  const out: OrderActivityImageRef[] = [];
  const files = item.activity_files;
  if (!Array.isArray(files)) return out;
  for (const f of files) {
    if (!f || typeof f !== 'object') continue;
    const rawUrl = (f as Meta).url;
    if (rawUrl == null || rawUrl === '') continue;
    const p = String(rawUrl).trim();
    const norm = normalizeStoragePath(p);
    if (!norm || seen.has(norm)) continue;
    seen.add(norm);
    const name =
      typeof (f as Meta).name === 'string' && (f as Meta).name.trim().length > 0
        ? String((f as Meta).name).trim()
        : (p.split(/[/\\]/).pop() ?? 'File');
    out.push({ url: s3Url(p) ?? p, label: name });
  }
  return out;
}

function productInfoNameFromItem(item: Meta): string | undefined {
  const pi = item.product_info;
  if (pi && typeof pi === 'object' && typeof (pi as Meta).name === 'string') {
    const n = String((pi as Meta).name).trim();
    return n || undefined;
  }
  return undefined;
}

/**
 * When backend sends scope ids, only those activity_items rows apply to this event
 * (e.g. samples_rejected / samples_accepted / activity_factory_product_scope).
 */
function filterActivityItemsByFactoryScope(items: Meta[], meta: Meta): Meta[] {
  const idSet = new Set<string>();
  for (const key of ['samples_accepted', 'samples_rejected', 'activity_factory_product_scope'] as const) {
    const arr = meta[key];
    if (!Array.isArray(arr)) continue;
    for (const x of arr) {
      if (x != null && String(x).trim() !== '') idSet.add(String(x).trim());
    }
  }
  if (idSet.size === 0) return items;
  const filtered = items.filter((it) => {
    const fid = it.factory_product_id;
    if (fid == null) return false;
    return idSet.has(String(fid).trim());
  });
  return filtered.length > 0 ? filtered : items;
}

function activityItemsHasPreviewContent(meta: Meta): boolean {
  if (imagesFromUploadedPaths(meta).length > 0) return true;
  const items = safeNonEmptyArray<Meta>(meta.activity_items);
  if (!items) return false;
  const scoped = filterActivityItemsByFactoryScope(items, meta);
  return scoped.some(
    (it) =>
      extractActivityFilesImagesOnly(it).length > 0 ||
      Boolean(productInfoNameFromItem(it)) ||
      Boolean(optionalNonEmptyString(it.message)),
  );
}

function sampleOutcomeLabel(actionType: string): string | undefined {
  if (actionType === 'customer_approved') return 'Approved';
  if (actionType === 'customer_rejected') return 'Rejected';
  if (actionType === 'submitted_for_customer_review') return 'Submitted';
  return undefined;
}

function buildCustomerSampleCards(actionType: string, meta: Meta): OrderActivitySamplePreviewCard[] | undefined {
  const rawItems = safeNonEmptyArray<Meta>(meta.activity_items);
  if (!rawItems?.length) return undefined;

  const wantCards =
    (actionType === 'customer_approved' || actionType === 'customer_rejected') && isCustomerPortalSamples(meta)
      ? true
      : actionType === 'submitted_for_customer_review' && meta.activity_source !== 'third_party_email';

  if (!wantCards) return undefined;

  const items = filterActivityItemsByFactoryScope(rawItems, meta);
  const outcome = sampleOutcomeLabel(actionType);

  const uploadedRefs = imagesFromUploadedPaths(meta);
  if (uploadedRefs.length > 0) {
    const first = items[0];
    let note = first ? optionalNonEmptyString(first.message) : undefined;
    if (!note && items.length === 1) {
      note = optionalNonEmptyString(meta.additional_notes);
    }
    const productName = first ? productInfoNameFromItem(first) : undefined;
    return [
      {
        outcome_label: outcome,
        product_name: productName,
        customer_note: note,
        preview_images: uploadedRefs,
      },
    ];
  }

  const cards: OrderActivitySamplePreviewCard[] = [];
  for (const it of items) {
    if (!it || typeof it !== 'object') continue;
    const productName = productInfoNameFromItem(it);
    let note = optionalNonEmptyString(it.message);
    if (!note && items.length === 1) {
      note = optionalNonEmptyString(meta.additional_notes);
    }
    const images = extractActivityFilesImagesOnly(it);
    if (!images.length && !productName && !note) continue;

    cards.push({
      outcome_label: outcome,
      product_name: productName,
      customer_note: note,
      preview_images: images,
    });
  }

  return cards.length ? cards : undefined;
}

function resolveDetailReason(actionType: string, meta: Meta): string | undefined {
  const direct = optionalNonEmptyString(meta.reason);
  if (direct) return direct;
  if (
    (actionType === 'customer_approved' || actionType === 'customer_rejected') &&
    isCustomerPortalSamples(meta)
  ) {
    return optionalNonEmptyString(meta.additional_notes);
  }
  return undefined;
}

function buildApprovedImagesFromMeta(actionType: string, meta: Meta): OrderActivityImageRef[] | undefined {
  if (actionType !== 'submitted_for_customer_review') return undefined;
  if (meta.activity_source === 'third_party_email') return undefined;
  if (activityItemsHasPreviewContent(meta)) return undefined;
  const paths = safeNonEmptyArray<string>(meta.uploaded_image_paths);
  if (!paths) return undefined;
  return paths.map((url, idx) => ({
    url: s3Url(String(url)) ?? String(url),
    label: `Uploaded ${idx + 1}`,
  }));
}

function buildDetailTables(actionType: string, meta: Meta): OrderActivityDetailTable[] | undefined {
  const tables: OrderActivityDetailTable[] = [];

  if ((actionType === 'customer_approved' || actionType === 'customer_rejected') && isCustomerPortalSamples(meta)) {
    const samples = safeNonEmptyArray<Meta>(meta.samples_by_status);
    if (samples) {
      tables.push({
        title: 'Samples',
        columns: ['Status', 'Feedback'],
        rows: samples.map((r) => [
          humanizeStatus(String(r.status ?? '')),
          optionalNonEmptyString(r.feedback) ?? toDisplayString(r.feedback),
        ]),
      });
    }
  }

  if (actionType === 'third_party_approved' || actionType === 'third_party_rejected') {
    const details = safeNonEmptyArray<Meta>(meta.approval_details);
    if (details) {
      tables.push({
        title: 'Approval details',
        columns: ['Product ID', 'Status', 'Feedback'],
        rows: details.map((r) => [
          String(r.factory_product_id ?? '—'),
          humanizeStatus(String(r.status ?? '')),
          optionalNonEmptyString(r.feedback) ?? toDisplayString(r.feedback),
        ]),
      });
    }
  }

  return tables.length ? tables : undefined;
}

function buildQcPdfReport(actionType: string, meta: Meta): OrderActivityQcPdfReport | undefined {
  if (actionType !== 'quality_control') return undefined;
  const raw = optionalNonEmptyString(meta.pdf_link);
  if (!raw) return undefined;
  const href = /^https?:\/\//i.test(raw) ? raw : (s3Url(raw) ?? raw);
  return { url: raw, href };
}

function buildTechpackPdfUrls(
  actionType: string,
  meta: Meta,
): OrderActivityTechpackPdfUrlsUpdate | undefined {
  if (actionType === 'techpack_edited') {
    const prevFromApi = optionalNonEmptyString(meta.previous_merchant_techpack_pdf_url);
    const latestFromApi = optionalNonEmptyString(meta.latest_merchant_techpack_pdf_url);
    const prevMerchant =
      prevFromApi ?? optionalNonEmptyString(meta.previous_merchant_pdf_url);
    const nextMerchant =
      latestFromApi ?? optionalNonEmptyString(meta.new_merchant_pdf_url);

    const order_item_ids_updated = Array.isArray(meta.order_item_ids_updated)
      ? meta.order_item_ids_updated
          .map((x) => Number(x))
          .filter((n) => Number.isFinite(n))
      : undefined;

    if (!prevMerchant && !nextMerchant && !(order_item_ids_updated?.length)) {
      return undefined;
    }

    return {
      previous_merchant_pdf_url: prevMerchant,
      new_merchant_pdf_url: nextMerchant,
      previous_merchant_pdf_href: prevMerchant ? s3Url(prevMerchant) : undefined,
      new_merchant_pdf_href: nextMerchant ? s3Url(nextMerchant) : undefined,
      factory_rows: [],
      ...(order_item_ids_updated?.length ? { order_item_ids_updated } : {}),
    };
  }

  if (actionType !== 'techpack_pdf_urls_updated') return undefined;

  const factory_rows: OrderActivityFactoryTechpackPdfRow[] = [];
  const rawList = meta.factory_by_order_item;
  if (Array.isArray(rawList)) {
    for (const item of rawList) {
      if (!item || typeof item !== 'object') continue;
      const row = item as Meta;
      const oid = Number(row.order_item_id);
      if (!Number.isFinite(oid)) continue;
      const prevPath = row.previous_pdf_url;
      const nextPath = row.new_pdf_url;
      const prevStr =
        typeof prevPath === 'string' && prevPath.trim().length > 0 ? prevPath.trim() : undefined;
      const nextStr =
        typeof nextPath === 'string' && nextPath.trim().length > 0 ? nextPath.trim() : undefined;
      factory_rows.push({
        order_item_id: oid,
        generation_pending: safeBool(row.generation_pending),
        previous_pdf_url: prevStr,
        new_pdf_url: nextStr,
        previous_pdf_href: s3Url(prevStr),
        new_pdf_href: s3Url(nextStr),
      });
    }
  }

  const prevM = meta.previous_merchant_pdf_url;
  const nextM = meta.new_merchant_pdf_url;
  const prevMerchant =
    typeof prevM === 'string' && prevM.trim().length > 0 ? prevM.trim() : undefined;
  const nextMerchant =
    typeof nextM === 'string' && nextM.trim().length > 0 ? nextM.trim() : undefined;

  const rawNote = meta.comparison_note;
  const comparison_note =
    typeof rawNote === 'string' && rawNote.trim().length > 0
      ? humanizeStatus(rawNote.trim())
      : undefined;

  return {
    comparison_note,
    generation_pending: safeBool(meta.generation_pending),
    merchant_generation_pending: safeBool(meta.merchant_generation_pending),
    previous_merchant_pdf_url: prevMerchant,
    new_merchant_pdf_url: nextMerchant,
    previous_merchant_pdf_href: s3Url(prevMerchant),
    new_merchant_pdf_href: s3Url(nextMerchant),
    factory_rows,
  };
}

/* --------------------------- entry points -------------------------- */

export function toListItem(row: BackendOrderActivityLog): OrderActivityLogListItem {
  const meta = asMeta(row);
  const action_type = row.action_type;
  return {
    id: String(row.id),
    title: pickTitle(action_type, meta),
    description: pickDescription(action_type, meta),
    occurred_at: row.created_at,
    user: buildUser(row, meta),
    activity_type: pickActivityTypeBadge(action_type, meta),
    activity_kind: pickKind(action_type, meta),
    action_type,
    activity_source: typeof meta.activity_source === 'string' ? meta.activity_source : undefined,
  };
}

export function toDetail(row: BackendOrderActivityLog): OrderActivityLogDetail {
  const base = toListItem(row);
  const meta = asMeta(row);
  const action_type = row.action_type;
  const states = buildStateEntries(action_type, meta);

  const approvedFromPaths = buildApprovedImagesFromMeta(action_type, meta);
  const sampleCards = buildCustomerSampleCards(action_type, meta);
  const detailTables = buildDetailTables(action_type, meta);
  const qcReport = buildQcPdfReport(action_type, meta);
  const priceUpdate = buildPriceUpdateDetail(action_type, meta);

  const detail: OrderActivityLogDetail = {
    ...base,
    field_changes: buildFieldChanges(action_type, meta),
    pdf_changes: buildPdfChanges(action_type, meta),
    factory: buildFactory(action_type, meta),
    notify_customer:
      action_type === 'production_date_changed'
        ? meta.action_taken === 'Save & Notify'
        : undefined,
    shipping_snapshot: buildShippingSnapshot(action_type, meta),
    tracking_link: buildTrackingLink(action_type, meta),
    delivery_box: buildDeliveryBox(action_type, meta),
    comment: buildComment(action_type, meta),
    reorder: buildReorder(action_type, meta),
    reason: resolveDetailReason(action_type, meta),
    previous_state: states.previous,
    new_state: states.next,
    metadata_lines: buildMetadataLines(action_type, meta, row),
    techpack_pdf_urls: buildTechpackPdfUrls(action_type, meta),
    ...(approvedFromPaths?.length ? { approved_images: approvedFromPaths } : {}),
    ...(sampleCards ? { customer_sample_cards: sampleCards } : {}),
    ...(detailTables ? { detail_tables: detailTables } : {}),
    ...(qcReport ? { qc_pdf_report: qcReport } : {}),
    ...(priceUpdate ? { price_update: priceUpdate } : {}),
  };

  return detail;
}
